/**
 * Phase 1.7 — auth guard that selects between the v1 AppLayout and the v2
 * AppShell based on the layout_v2 feature flag.
 *
 * Rendering rules:
 *   - while flag fetch is in flight → blank background (no flicker)
 *   - flag === 'v1' → legacy AppLayout (unchanged from Phase 1.6)
 *   - flag === 'v2' → AppShell with optional right rail passed through
 *
 * Pages stay agnostic — they pass `rail` always and the v1 path simply
 * ignores it.
 */

import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, checkAuth } from '@/lib/auth';
import { useLayout } from '@/lib/layout-flag';
import { AppLayout } from '@/components/layout/AppLayout';
import { AppShell } from './layout/AppShell';

interface AuthGuardV2Props {
  children: ReactNode;
  rail?: ReactNode;
  fullBleed?: boolean;
}

export function AuthGuardV2({ children, rail, fullBleed }: AuthGuardV2Props): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const layout = useLayout();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isInitialized) checkAuth();
  }, [isInitialized]);

  useEffect(() => {
    if (isInitialized && !isLoading && !user) navigate('/login', { replace: true });
  }, [isInitialized, isLoading, user, navigate]);

  // Layout flag still loading → blank canvas. Same flicker policy as the
  // v1 splash, but we don't render the brand splash here because that's a
  // pre-auth concern.
  if (layout === null || !isInitialized || isLoading) {
    return <div className="min-h-screen bg-sand dark:bg-sand-dark" />;
  }
  if (!user) return null;

  if (layout === 'v2') {
    return <AppShell rail={rail} fullBleed={fullBleed}>{children}</AppShell>;
  }
  return <AppLayout>{children}</AppLayout>;
}
